import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, User, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "@/lib/store";
import { translations, formatPrice, BRAND } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CartDrawer() {
  const {
    lang,
    cart,
    cartOpen,
    setCartOpen,
    cartTotal,
    cartCount,
    updateQty,
    removeFromCart,
  } = useStore();
  const t = translations[lang];

  const [customerName, setCustomerName] = useState(() => localStorage.getItem("bz_customer_name") || "");
  const [customerPhone, setCustomerPhone] = useState(() => localStorage.getItem("bz_customer_phone") || "");

  useEffect(() => {
    localStorage.setItem("bz_customer_name", customerName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName]);

  useEffect(() => {
    localStorage.setItem("bz_customer_phone", customerPhone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPhone]);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    const phoneDigits = (customerPhone || "").replace(/\D/g, "");
    if (!customerName.trim() || phoneDigits.length < 8) {
      toast.error(t.cart_customer_required);
      return;
    }
    // Record the order intent in the backend (fire-and-forget, don't block UX)
    axios
      .post(`${API}/orders`, {
        items: cart.map((it) => ({
          slug: it.slug,
          name_fr: it.name_fr,
          name_en: it.name_en,
          price_gnf: it.price_gnf,
          quantity: it.quantity,
          size: it.size,
          color: it.color,
          image: it.image,
        })),
        total_gnf: cartTotal,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        lang,
        source: "whatsapp_cart",
      })
      .catch(() => {});

    const siteOrigin = typeof window !== "undefined" ? window.location.origin : "https://brafzak.shop";
    // Build per-product blocks. Image URL comes FIRST so WhatsApp uses it for preview.
    const blocks = cart.map((it, i) => {
      const name = lang === "fr" ? it.name_fr : it.name_en;
      const opts = [it.size, it.color].filter(Boolean).join(" / ");
      const subtotal = formatPrice(it.price_gnf * it.quantity, lang);
      const productUrl = `${siteOrigin}/product/${it.slug}`;
      let imgUrl = it.image || "";
      if (imgUrl) {
        if (imgUrl.startsWith("/api/")) imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
        else if (imgUrl.startsWith("/")) imgUrl = `${siteOrigin}${imgUrl}`;
      }
      const header = `━━━ Article ${i + 1}/${cart.length} ━━━`;
      const optsLine = opts ? `🎨 ${opts}\n` : "";
      const imgLine = imgUrl ? `📷 ${imgUrl}\n` : "";
      return `${header}\n📦 ${name}\n${optsLine}🔢 Quantité : ${it.quantity}\n💰 Sous-total : ${subtotal}\n🔗 ${productUrl}\n${imgLine}`;
    });
    const itemsCount = cart.reduce((s, it) => s + it.quantity, 0);
    const total = formatPrice(cartTotal, lang);
    const intro =
      lang === "fr"
        ? `🛒 NOUVELLE COMMANDE BRAF_ZAK 🛒\n\n👤 Client : ${customerName.trim()}\n📞 Tél : ${customerPhone.trim()}\n📦 ${itemsCount} article${itemsCount > 1 ? "s" : ""} commandé${itemsCount > 1 ? "s" : ""}`
        : `🛒 NEW BRAF_ZAK ORDER 🛒\n\n👤 Customer: ${customerName.trim()}\n📞 Phone: ${customerPhone.trim()}\n📦 ${itemsCount} item${itemsCount > 1 ? "s" : ""} ordered`;
    const totalLabel = lang === "fr" ? "💎 TOTAL" : "💎 TOTAL";
    const closing =
      lang === "fr"
        ? "✅ Un membre de l'équipe Braf_Zak vous répondra rapidement.\n⚽👑 brafzak.shop"
        : "✅ A Braf_Zak team member will reply shortly.\n⚽👑 brafzak.shop";
    const text = [intro, "", ...blocks, `${totalLabel} : ${total}`, "", closing].join("\n");
    const url = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(text)}`;
    toast.success(
      lang === "fr"
        ? "Merci d'avoir contacté brafzak.shop ! Vous êtes redirigé(e) vers WhatsApp…"
        : "Thanks for contacting brafzak.shop! Redirecting to WhatsApp…",
      { duration: 4000 }
    );
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md border-l border-yellow-600/30 text-white p-0 flex flex-col"
        style={{ background: "linear-gradient(180deg, #0a0805, #050505)" }}
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-yellow-600/20">
          <SheetTitle
            className="flex items-center gap-3 text-2xl"
            style={{ fontFamily: '"Bebas Neue", system-ui', color: "#F4D77A", letterSpacing: "0.06em" }}
          >
            <ShoppingBag className="w-5 h-5" />
            {t.cart_title}
            <span className="text-sm text-white/50 ml-auto">
              {cartCount} {t.qty}
            </span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t.cart_checkout_hint}
          </SheetDescription>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
            <div className="w-20 h-20 rounded-full border border-yellow-500/30 flex items-center justify-center">
              <ShoppingBag className="w-9 h-9 text-yellow-400/70" />
            </div>
            <p className="text-white/70" data-testid="cart-empty-text">{t.cart_empty}</p>
            <Link
              to="/shop"
              onClick={() => setCartOpen(false)}
              data-testid="cart-empty-cta"
              className="px-6 py-3 rounded-full text-sm font-bold tracking-widest text-black"
              style={{
                background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                boxShadow: "0 8px 20px -6px rgba(212,175,55,0.55)",
              }}
            >
              {t.cart_empty_cta}
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {cart.map((it) => {
                const name = lang === "fr" ? it.name_fr : it.name_en;
                return (
                  <div
                    key={it.key}
                    data-testid={`cart-item-${it.slug}`}
                    className="flex gap-3 p-3 rounded-xl border border-yellow-500/15 bg-white/[0.02]"
                  >
                    <div
                      className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center text-yellow-400"
                      style={{
                        background:
                          "radial-gradient(circle at 30% 20%, #1a1408 0%, #050505 70%)",
                      }}
                    >
                      {it.image ? (
                        <img src={it.image} alt={name} className="w-full h-full object-cover" />
                      ) : (
                        <span
                          className="text-xl font-black"
                          style={{ fontFamily: '"Bebas Neue", system-ui' }}
                        >
                          BZ
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-sm text-yellow-100 truncate">{name}</h4>
                      <p className="text-xs text-white/50 mt-0.5">
                        {[it.size, it.color].filter(Boolean).join(" · ")}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1 border border-yellow-500/25 rounded-full">
                          <button
                            data-testid={`cart-qty-dec-${it.slug}`}
                            onClick={() => updateQty(it.key, it.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center text-yellow-300 hover:bg-yellow-500/10 rounded-full"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-xs font-bold w-6 text-center">
                            {it.quantity}
                          </span>
                          <button
                            data-testid={`cart-qty-inc-${it.slug}`}
                            onClick={() => updateQty(it.key, it.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center text-yellow-300 hover:bg-yellow-500/10 rounded-full"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-300 font-bold text-sm">
                            {formatPrice(it.price_gnf * it.quantity, lang)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`cart-remove-${it.slug}`}
                      onClick={() => removeFromCart(it.key)}
                      className="text-white/40 hover:text-red-400 self-start"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-yellow-600/20 px-6 py-5 space-y-4 bg-black/40">
              <div className="space-y-2.5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-yellow-300/80">
                  {t.cart_customer_section}
                </p>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-yellow-300/60" />
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder={t.cart_customer_name}
                    data-testid="cart-customer-name-input"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/5 border border-yellow-500/25 text-white text-sm placeholder-white/40 focus:outline-none focus:border-yellow-400"
                  />
                </div>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-yellow-300/60" />
                  <input
                    type="tel"
                    required
                    inputMode="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder={t.cart_customer_phone}
                    data-testid="cart-customer-phone-input"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/5 border border-yellow-500/25 text-white text-sm placeholder-white/40 focus:outline-none focus:border-yellow-400"
                  />
                </div>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-sm tracking-[0.2em] uppercase text-white/60">
                  {t.cart_total}
                </span>
                <span
                  className="text-2xl font-black"
                  style={{ fontFamily: '"Bebas Neue", system-ui', color: "#F4D77A" }}
                  data-testid="cart-total"
                >
                  {formatPrice(cartTotal, lang)}
                </span>
              </div>
              <Button
                onClick={handleCheckout}
                data-testid="cart-checkout-btn"
                className="w-full rounded-full font-bold tracking-widest text-black hover:scale-[1.01] transition-transform py-6"
                style={{
                  background: "linear-gradient(180deg, #25D366 0%, #128C7E 100%)",
                  color: "white",
                  boxShadow: "0 8px 24px -6px rgba(37,211,102,0.5)",
                }}
              >
                {t.cart_checkout}
              </Button>
              <p className="text-[10px] text-center text-white/40 tracking-wider">
                {t.cart_checkout_hint}
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
